import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useStore } from '../store/store';
import { setVariablesView } from '../undo/undoHistory';
import Loader from '../Loader';
import { sidebarWidth } from '../constants/gridConfig';

const leftShownStatus = {
  SUMMARY: "summary",
  SECTIONS: "sections",
  EXPORT: "export",
  DOCUMENTS: "documents",
  ELEMENTS: "elements",
  LAYOUTS: "layouts",
  SETTINGS: "settings",
  VARIABLES: "variables"
}

const defaultWidth = sidebarWidth
const isDefaultCollapsed = false;

const defaultProps = {
  width: defaultWidth,
  left: {
    collapsed: isDefaultCollapsed,
    shownStatus: leftShownStatus["DOCUMENTS"]
  },
  right: {
    collapsed: isDefaultCollapsed,
  },
}

// Même forme que la valeur du provider (cf. OverlayProvider) : un composant
// monté hors de lui (iframe de composition de forme) lit `overlayProps`.
const OverlayContext = React.createContext({
  overlayProps: defaultProps,
  handleOverlayCollapse: (side, collapsed) => null,
  updateLeftShownStatus: (status, boxId) => null,
  showLeftPanel: (status) => null
});

const useOverlay = () => {
  const context = React.useContext(OverlayContext);

  if (!context) {
    throw new Error(`useOverlay must be used within a OverlayProvider`);
  }

  return context;
};

// Vue formulaire : le panneau gauche est sur les variables et le document n'est
// pas un modèle — en modèle, cet onglet sert à ranger les variables et le
// document reste affiché, c'est seulement en proposition que le formulaire
// prend toute la place (cf. Middle).
const useIsFormView = () => {
  const { overlayProps } = useOverlay();
  const isTemplate = useStore(({ currentDocument }) => currentDocument.type === 'template');

  return overlayProps.left.shownStatus === leftShownStatus.VARIABLES && !isTemplate;
};

const selector = ({ handleBoxVariableSelectedId }) => ({
  handleBoxVariableSelectedId
})

const OverlayProvider = ({ children }) => {
  const { handleBoxVariableSelectedId } = useStore(selector)
  const areSectionsActivating = useStore(({ areSectionsActivating }) => areSectionsActivating)
  const isDocumentLoading = useStore(({ isDocumentLoading }) => isDocumentLoading)
  const loadingData = useStore(({ loadingData }) => loadingData)
  const isTemplate = useStore(({ currentDocument }) => currentDocument.type === 'template')
  const defaultDocumentView = useStore(({ configuration }) => configuration.defaultDocumentView)
  const isPreviewLoading = useStore(({ isPreviewLoading }) => isPreviewLoading)
  // Génération / enregistrement du PDF : l'app est bloquée jusqu'à la réponse
  // (websocket ou PDF local) qui remet PDFRenderState à false. L'aperçu, lui,
  // passe par isPreviewLoading.
  const isPdfGenerating = useStore(({ PDFRenderState }) => PDFRenderState === 'visualize' || PDFRenderState === 'archive')


  const [overlayProps, setOverlayProps] = useState({
    ...defaultProps,
  });

  // Modèle ou section : on arrive sur les éléments, c'est là qu'on construit.
  // Sur un modèle, la vue par défaut du document ne change rien à ce choix : la
  // basculer depuis le panneau Paramètres ne doit pas fermer ce panneau.
  const wasTemplate = useRef(null)
  useEffect(() => {
    const stayedOnTemplate = isTemplate && wasTemplate.current === true
    wasTemplate.current = isTemplate
    if (stayedOnTemplate) return
    const proposalStatus = defaultDocumentView === 'form' ? leftShownStatus["VARIABLES"] : leftShownStatus["DOCUMENTS"]
    setOverlayProps((old) => ({
      ...old,
      left: {
        ...old.left,
        shownStatus: isTemplate ? leftShownStatus["ELEMENTS"] : proposalStatus
      },
    }))
  }, [defaultDocumentView, isTemplate])

  // Miroir synchrone du panneau gauche vers le moteur do/undo : la capture
  // (subscriber pur-store) ne voit pas ce state React. Sert à marquer l'undo
  // d'une box éditée au formulaire pour rouvrir la vue Variables.
  useEffect(() => {
    setVariablesView(overlayProps.left.shownStatus === leftShownStatus["VARIABLES"])
  }, [overlayProps.left.shownStatus])

  const handleOverlayCollapse = useCallback((side, collapsed) => {
    if (side !== "left" && side !== "right") return;

    setOverlayProps((oldProps) => ({
      ...oldProps,
      [side]: {
        ...oldProps[side],
        collapsed
      }
    }))
    if (side === "right")
      handleBoxVariableSelectedId();
  }, [handleBoxVariableSelectedId]);

  // Ouvre un panneau du rail gauche sans toucher à la sélection. À préférer à
  // updateLeftShownStatus quand on veut seulement montrer un panneau : lui remet
  // aussi à zéro la sélection de variable, ce qui désélectionne la box en cours
  // en modèle.
  const showLeftPanel = useCallback((status) => {
    if (!Object.values(leftShownStatus).includes(status)) return;

    setOverlayProps((oldProps) => ({
      ...oldProps,
      left: {
        ...oldProps.left,
        shownStatus: status,
        collapsed: false
      }
    }))
  }, [])

  const updateLeftShownStatus = useCallback((status, boxId = undefined) => {
    if (!Object.values(leftShownStatus).includes(status)) return;

    showLeftPanel(status);

    // Lorsque l'on ouvre la vue "variables" avec une box donnée,
    // on active explicitement le scroll automatique dans le FormViewer.
    const shouldScroll = status === leftShownStatus["VARIABLES"] && !!boxId;
    handleBoxVariableSelectedId(boxId, shouldScroll);
  }, [showLeftPanel, handleBoxVariableSelectedId])

  const value = React.useMemo(
    () => ({ overlayProps, handleOverlayCollapse, updateLeftShownStatus, showLeftPanel }),
    [overlayProps, handleOverlayCollapse, updateLeftShownStatus, showLeftPanel]
  );

  const isLoading = areSectionsActivating || isDocumentLoading || isPreviewLoading || isPdfGenerating || loadingData;

  return (
    <OverlayContext.Provider value={value}>
      {isLoading &&
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 99999,
            backgroundColor: 'rgba(0, 0, 0, 0.25)', // fond grisé pour indiquer que l'app est bloquée
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'auto', // capte tous les clics pour bloquer les interactions
          }}
        >
          <Loader style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'transparent', // transparent car le fond grisé est sur le div parent
            width: "auto",
            height: "auto",
            position: 'relative', // relative car le position fixed est sur le parent
          }} />
        </div>
      }
      {children}
    </OverlayContext.Provider>
  );
};

// Le contexte lui-même est exporté pour les consommateurs qui peuvent tourner
// hors du Layout (hooks partagés) : useOverlay lève, alors que useContext rend
// la valeur par défaut, dont les actions sont des no-op.
export { OverlayContext, OverlayProvider, useOverlay, useIsFormView, leftShownStatus };
